/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Reach of explicit allow grants: everyone when every deny-by-default policy's audience is covered (an all-users grant, or grants naming the policy's whole audience), selected when grants free only part of an audience, none without grants. A role grant whose membership happens to span the organization still reads selected — reach compares principal sets, not expanded memberships.
 */
export const AllowedFor = {
  Everyone: "everyone",
  Selected: "selected",
  None: "none",
} as const;
/**
 * Reach of explicit allow grants: everyone when every deny-by-default policy's audience is covered (an all-users grant, or grants naming the policy's whole audience), selected when grants free only part of an audience, none without grants. A role grant whose membership happens to span the organization still reads selected — reach compares principal sets, not expanded memberships.
 */
export type AllowedFor = OpenEnum<typeof AllowedFor>;

/**
 * Reach of explicit block mechanisms: an everyone-audience block rule, a targeted rule or targeted deny-by-default policy, or none.
 */
export const BlockedFor = {
  Everyone: "everyone",
  Some: "some",
  None: "none",
} as const;
/**
 * Reach of explicit block mechanisms: an everyone-audience block rule, a targeted rule or targeted deny-by-default policy, or none.
 */
export type BlockedFor = OpenEnum<typeof BlockedFor>;

/**
 * What happens to a user no rule names: deny under an everyone-audience deny-by-default policy, allow when blocking exists without one, none when no blocking policy is enabled.
 */
export const BlockingDefault = {
  Deny: "deny",
  Allow: "allow",
  None: "none",
} as const;
/**
 * What happens to a user no rule names: deny under an everyone-audience deny-by-default policy, allow when blocking exists without one, none when no blocking policy is enabled.
 */
export type BlockingDefault = OpenEnum<typeof BlockingDefault>;

/**
 * The recorded review decision, when one exists.
 */
export const Decision = {
  Approved: "approved",
  Denied: "denied",
} as const;
/**
 * The recorded review decision, when one exists.
 */
export type Decision = OpenEnum<typeof Decision>;

/**
 * How much of the recorded decision enforcement delivers. full: the decision's own writes are intact — an approval's grants survive unoverridden (a scoped blast radius is the decision as recorded, not a shortfall), or a denial lands as a project-wide block. partial: something carries the decision but not all of it, such as a denial only a targeted policy enforces, or an approval whose grants were later removed or overridden. none: nothing carries it — no blocking policy exists, the target is a local command (stdio decisions are recorded without writing enforcement), or no decision is recorded at all.
 */
export const DecisionCoverage = {
  Full: "full",
  Partial: "partial",
  None: "none",
} as const;
/**
 * How much of the recorded decision enforcement delivers. full: the decision's own writes are intact — an approval's grants survive unoverridden (a scoped blast radius is the decision as recorded, not a shortfall), or a denial lands as a project-wide block. partial: something carries the decision but not all of it, such as a denial only a targeted policy enforces, or an approval whose grants were later removed or overridden. none: nothing carries it — no blocking policy exists, the target is a local command (stdio decisions are recorded without writing enforcement), or no decision is recorded at all.
 */
export type DecisionCoverage = OpenEnum<typeof DecisionCoverage>;

/**
 * The shape of the user-to-access function: allowed and blocked are uniform, restricted varies by user, unenforced means no blocking policy applies.
 */
export const State = {
  Allowed: "allowed",
  Restricted: "restricted",
  Blocked: "blocked",
  Unenforced: "unenforced",
} as const;
/**
 * The shape of the user-to-access function: allowed and blocked are uniform, restricted varies by user, unenforced means no blocking policy applies.
 */
export type State = OpenEnum<typeof State>;

/**
 * The enforcement verdict for a shadow MCP server, computed server-side from policies, grants, and the recorded decision. state is the canonical compression of who may call the server; the remaining fields name the mechanisms so a client renders wording without re-deriving enforcement.
 */
export type ShadowMCPAccessSummary = {
  /**
   * Reach of explicit allow grants: everyone when every deny-by-default policy's audience is covered (an all-users grant, or grants naming the policy's whole audience), selected when grants free only part of an audience, none without grants. A role grant whose membership happens to span the organization still reads selected — reach compares principal sets, not expanded memberships.
   */
  allowedFor: AllowedFor;
  /**
   * Reach of explicit block mechanisms: an everyone-audience block rule, a targeted rule or targeted deny-by-default policy, or none.
   */
  blockedFor: BlockedFor;
  /**
   * What happens to a user no rule names: deny under an everyone-audience deny-by-default policy, allow when blocking exists without one, none when no blocking policy is enabled.
   */
  blockingDefault: BlockingDefault;
  /**
   * The recorded review decision, when one exists.
   */
  decision?: Decision | undefined;
  /**
   * How much of the recorded decision enforcement delivers. full: the decision's own writes are intact — an approval's grants survive unoverridden (a scoped blast radius is the decision as recorded, not a shortfall), or a denial lands as a project-wide block. partial: something carries the decision but not all of it, such as a denial only a targeted policy enforces, or an approval whose grants were later removed or overridden. none: nothing carries it — no blocking policy exists, the target is a local command (stdio decisions are recorded without writing enforcement), or no decision is recorded at all.
   */
  decisionCoverage: DecisionCoverage;
  /**
   * The shape of the user-to-access function: allowed and blocked are uniform, restricted varies by user, unenforced means no blocking policy applies.
   */
  state: State;
};

/** @internal */
export const AllowedFor$inboundSchema: z.ZodMiniType<AllowedFor, unknown> =
  openEnums.inboundSchema(AllowedFor);

/** @internal */
export const BlockedFor$inboundSchema: z.ZodMiniType<BlockedFor, unknown> =
  openEnums.inboundSchema(BlockedFor);

/** @internal */
export const BlockingDefault$inboundSchema: z.ZodMiniType<
  BlockingDefault,
  unknown
> = openEnums.inboundSchema(BlockingDefault);

/** @internal */
export const Decision$inboundSchema: z.ZodMiniType<Decision, unknown> =
  openEnums.inboundSchema(Decision);

/** @internal */
export const DecisionCoverage$inboundSchema: z.ZodMiniType<
  DecisionCoverage,
  unknown
> = openEnums.inboundSchema(DecisionCoverage);

/** @internal */
export const State$inboundSchema: z.ZodMiniType<State, unknown> = openEnums
  .inboundSchema(State);

/** @internal */
export const ShadowMCPAccessSummary$inboundSchema: z.ZodMiniType<
  ShadowMCPAccessSummary,
  unknown
> = z.pipe(
  z.object({
    allowed_for: AllowedFor$inboundSchema,
    blocked_for: BlockedFor$inboundSchema,
    blocking_default: BlockingDefault$inboundSchema,
    decision: z.optional(Decision$inboundSchema),
    decision_coverage: DecisionCoverage$inboundSchema,
    state: State$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "allowed_for": "allowedFor",
      "blocked_for": "blockedFor",
      "blocking_default": "blockingDefault",
      "decision_coverage": "decisionCoverage",
    });
  }),
);

export function shadowMCPAccessSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ShadowMCPAccessSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ShadowMCPAccessSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ShadowMCPAccessSummary' from JSON`,
  );
}
